import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faUpload } from "@fortawesome/free-solid-svg-icons";

import IconUploadArea from "./icons/IconUploadArea";
import IconUploadFile from "./icons/IconUploadFile";
import IconCopyPasteData from "./icons/IconCopyPasteData";
import styles from "../Style/ImportFile.module.css";

function ImportFile() {
  const [file, setFile] = useState(null);
  const [text, setText] = useState({ rawText: "", formattedForAPI: "" });
  const [isTextArea, setIsTextArea] = useState(false);
  const [customerCode, setCustomerCode] = useState("");
  const [customerCodes, setCustomerCodes] = useState([]);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [unmatchedProductsMessage, setUnmatchedProductsMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerCodes = async () => {
      try {
        const response = await axios.get("https://forethought.foremost-uk.com/api/customers");
        setCustomerCodes(response.data);
      } catch (error) {
        console.error("Error fetching customer codes:", error);
      }
    };

    fetchCustomerCodes();
  }, []);

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    const validExtensions = ["csv", "xls", "xlsx"];
    const fileExtension = selectedFile?.name.split(".").pop().toLowerCase();

    if (selectedFile && validExtensions.includes(fileExtension)) {
      setFile(selectedFile);
      setIsFileUploaded(true);
    } else {
      alert("Please upload a valid Excel file (.csv, .xls, .xlsx)");
      setFile(null);
      setIsFileUploaded(false);
    }
  };

  const handleCustomerCodeChange = (event) => {
    setCustomerCode(event.target.value);
  };

  const goToProductComparisonSummary = async () => {
    if (!customerCode) {
      alert("Please select a Customer");
      return;
    }

    if (!file && !text.rawText) {
      alert("Please upload a file or paste text before proceeding.");
      return;
    }

    try {
      await axios.post("https://forethought.foremost-uk.com/api/add-input", {
        customer_code: customerCode,
      });

      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post("https://forethought.foremost-uk.com/api/upload-details", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else if (text.rawText) {
        const rows = text.formattedForAPI
          .split("</tr>")
          .filter((row) => row.trim())
          .map((row) => {
            const cells = row.replace(/<[^>]*>/g, "").split("\t");
            return { data: cells[0] };
          });

        await axios.post("https://forethought.foremost-uk.com/api/addDetailInput", { rows });
      }

      const response = await axios.get(
        "https://forethought.foremost-uk.com/api/product-comparison-summary"
      );
      const data = response.data;

      if (data.unmatchedProducts && data.unmatchedProducts.length > 0) {
        setUnmatchedProductsMessage(
          `The following products didn't match any existing products: ${data.unmatchedProducts.join(
            ", "
          )}`
        );
      } else {
        setUnmatchedProductsMessage("All products matched successfully.");
      }

      if (
        data &&
        data.total_price_1 !== undefined &&
        data.total_price_2 !== undefined
      ) {
        navigate("/PCS", {
          state: {
            currentState: {
              costTotal: `$${parseFloat(data.total_price_1).toFixed(2)}`,
              co2eTotal: `${data.total_co2_1.toFixed(2)} kg`,
              plasticTotal: `${data.total_plastic_1.toFixed(2)} kg`,
              hazardCodeScore: data.total_hazard_score_1,
            },
            futureState: {
              costTotal: `$${parseFloat(data.total_price_2).toFixed(2)}`,
              co2eTotal: `${data.total_co2_2.toFixed(2)} kg`,
              plasticTotal: `${data.total_plastic_2.toFixed(2)} kg`,
              hazardCodeScore: data.total_hazard_score_2,
            },
          },
        });
      } else {
        throw new Error("Invalid data structure received from the API");
      }
    } catch (error) {
      console.error("Error during submission:", error);
      alert("There was an error. Please try again.");
    }
  };

  const handlePaste = (event) => {
    const clipboardData = event.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("Text");

    const rows = pastedData.split("\n").map((row) => row.split("\t"));
    const tableRows = rows
      .map((row) => {
        const cells = row.map((cell) => `<td>${cell}</td>`).join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const tableHTML = `<table>${tableRows}</table>`;

    setText({ rawText: pastedData, formattedForAPI: tableHTML });
    event.preventDefault();
  };

  return (
    <>
      <div className="container">
        <div className="flex-container">
          <h1>Forethought Product Tool</h1>

          <div className={styles.dropdown}>
            <select
              id="customerCode"
              value={customerCode}
              onChange={handleCustomerCodeChange}
            >
              <option value="" disabled>
                Select Customer
              </option>
              {customerCodes.map((customer) => (
                <option
                  key={customer.customer_code}
                  value={customer.customer_code}
                >
                  {customer.customer_name}
                </option>
              ))}
            </select>
          </div>

          <input
            type="file"
            id="fileInput"
            accept=".csv, .xls, .xlsx"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />

          <div className="grid-container">
            <button
              onClick={() => document.getElementById("fileInput").click()}
              style={{
                backgroundColor: isFileUploaded ? "#e0e0e0" : "#f2f2f2",
                outline: isFileUploaded ? "1.5px solid #0039C7" : "none",
              }}
              className="action-card"
            >
              <div className="icon-container">
                {isFileUploaded ? (
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    size="2x"
                    color="#0039C7"
                  />
                ) : (
                  <IconUploadFile />
                )}
              </div>
              <div className="content">
                <p>{isFileUploaded ? "File Uploaded" : "Upload CSV"}</p>
              </div>
            </button>

            <button
              onClick={() => document.getElementById("fileInput").click()}
              className="action-card"
            >
              <div className="icon-container">
                <IconUploadFile />
              </div>
              <div className="content">
                <p>Upload PDF</p>
              </div>
            </button>
            <button
              onClick={() => setIsTextArea((prev) => !prev)}
              className="action-card"
            >
              {isTextArea ? (
                <>
                  <div className="icon-container">
                    <IconUploadArea size="85%" />
                  </div>
                  <div className="content">
                    <p>Drag & Drop</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="icon-container">
                    <IconCopyPasteData />
                  </div>
                  <div className="content">
                    <p>Copy & Paste Data</p>
                  </div>
                </>
              )}
            </button>
          </div>

          {isTextArea ? (
            <textarea
              placeholder="Type or Paste your Data here..."
              className={styles.pasteDataHere}
              onPaste={handlePaste}
              value={text.rawText}
              onChange={(e) =>
                setText((prev) => ({ ...prev, rawText: e.target.value }))
              }
            />
          ) : (
            <div className={styles.uploadArea}>
              <div style={{ width: "56px" }}>
                <IconUploadArea />
              </div>
              <p>Select an option above or drag and drop here</p>
            </div>
          )}

          <button onClick={goToProductComparisonSummary}>
            Start import and compare products
          </button>
        </div>
      </div>
    </>
  );
}

export default ImportFile;
